<!--
  @component
  Generates canvas dots onto a map using [d3-geo](https://github.com/d3/d3-geo).
 -->
<script>
	import { getContext } from "svelte";
	import { forceSimulation, forceX, forceY, forceManyBody, forceCollide, forceCenter } from "d3-force";
	import { elasticInOut } from "svelte/easing";
	import Point from "./Point.svelte";
	import { groupBy } from "../../../utils";

	const { data, width, height, xGet, rGet, xScale, yRange } = getContext("LayerCake");

	export let projection;
	export let showHighlights;
	export let color = "#7209b7";
	export let highlightColor = "#f20089";
	export let radiusFactor = 1;
	export let greyedOut;
	export let layout = "none";
	export let sizedUpOnEntry = true;

	export let highlighted = (props) => false;
	export let annotated = (props) => false;
	export let visible = (props) => true;

	let manyBodyStrength = 3;
	let xStrength = 0.1;
	let yStrength = 0.1;
	let nodeStrokeWidth = 0;

	const sizedUp = () => {
		return {
			css: (t) => {
				return `
         r: ${t * 6};
        `;
			},
			easing: elasticInOut,
			duration: 300,
		};
	};

	const offsetY = 3;
	const offsetXBoundary = 3;

	// ///////////////////////////////////////////////////////////
	// Make a copy because the simulation will alter the objects
	// ///////////////////////////////////////////////////////////
	let initialNodes = $data.features.map((d) => ({ ...d }));
	let simulation = forceSimulation(initialNodes).stop();
	let nodes = simulation.nodes();

	simulation.on("tick", (node) => {
		nodes = simulation.nodes();
	});

	// ///////////////////////////////////////////////////////////
	// When variables change, set forces and restart the simulation
	// ///////////////////////////////////////////////////////////
	let forceCharge;
	let forceCollision;
	let calcX;
	let calcY;
	let xForceStrength;
	let yForceStrength;
	let forceAlignBottom;
	let forcePositionMap;
	let alphaMin;
	let alpha;
	let alphaDecay;

	const forceBoundary = () => {
		nodes.forEach((node) => {
			const radius = $rGet(node.properties);
			const y = Math.max(radius, Math.min(Math.max(...$yRange) - offsetY - radius * 2, node.y));
			node.y = y;

			const x = Math.min($width - offsetXBoundary, node.x);
			node.x = x
		});
	};

	$: forceMap = () => {
		nodes.forEach((node) => {
			node.x = projection(node.geometry.coordinates)[0];
			node.y = projection(node.geometry.coordinates)[1];
		});
	};

	let radius;
	let histogramPadding;
	$: {
		histogramPadding = $width / 300;
		const yearRange = $xScale.domain()[1] - $xScale.domain()[0];
		if (layout === "histogram") {
			const widthScaled = $width / (2 * yearRange) - histogramPadding
			const heightScaled = $height / 30;
			const smaller = Math.min(widthScaled, heightScaled)
			
			radius = () => smaller > 0 ? smaller : 0
		} else {
			radius = point => $rGet(point.properties)
		}
	}

	$: {
		forceCharge = null;
		forceCollision = null;
		forceAlignBottom = null;
		forcePositionMap = null;
		calcX = (d) => $width / 2;
		calcY = (d) => Math.max(...$yRange);
		xForceStrength = xStrength;
		yForceStrength = yStrength;
		alpha = 1;
		alphaMin = 0.01;
		alphaDecay = 0.0228;

		// MAP LAYOUT
		if (layout === "map") {
			xForceStrength = 0;
			yForceStrength = 0;
			forcePositionMap = forceMap;
			alphaDecay = 0.05;
			alphaMin = 0.1;
			// BEESWARM LAYOUT
		} else if (layout === "beeswarm") {
			calcX = (d) => {
				const isBandwidth = $xScale && typeof $xScale.bandwidth === "function";
				return $xGet(d.properties) + (isBandwidth ? $xScale.bandwidth() / 2 : 0);
			};
			forceCharge = forceManyBody().strength(manyBodyStrength);
			forceCollision = forceCollide()
				.radius((d) => {
					return $rGet(d.properties) + nodeStrokeWidth + 2; // Divide this by two because an svg stroke is drawn halfway out
				})
				.iterations(5); // the higher the number, the less jittering is produced

			forceAlignBottom = forceBoundary;
			alphaDecay = 0.05;
			// STACKED LAYOUT
		} else {
			const simplifiedData = $data.features
				.map(({ id, properties }) => ({ id, year: properties.year, hl: highlighted(properties) }))
				.sort((a, b) => (a.hl ? 1 : -1));
			const groupedByYear = groupBy(simplifiedData, "year");
			calcX = (d) => {
				const isBandwidth = $xScale && typeof $xScale.bandwidth === "function";
				return $xGet(d.properties) + (isBandwidth ? $xScale.bandwidth() / 2 : 0);
			};
			calcY = (d) => {
				const year = d.properties.year;
				const index = groupedByYear[year]?.findIndex((data) => data.id === d.id);

				return Math.max(...$yRange) - offsetY - radius(d) - index * (radius(d) * 2 + histogramPadding);
			};
			alphaMin = 0.05;
		}
	}

	// ///////////////////////////////////////////////////////////
	// restart the simulation if the layout, the map position aka.
	// projection or the width changes
	// ///////////////////////////////////////////////////////////

	const restartSimulation = () => {
		simulation
			.force("x", xForceStrength ? forceX().x(calcX).strength(xForceStrength) : null)
			.force("y", yForceStrength ? forceY().y(calcY).strength(yForceStrength) : null)
			.force("collision", forceCollision)
			.force("align-bottom", forceAlignBottom)
			.force("map", forcePositionMap)
			.force("charge", forceCharge)
			.alpha(alpha)
			.alphaDecay(alphaDecay)
			.alphaMin(alphaMin)
			.restart();
	};

	$: layout, restartSimulation();
	$: projection && restartSimulation();
	$: $width, restartSimulation();

	// $: console.log(radius)
</script>

<g class="points">
	{#each nodes as point (point.id)}
		<Point
		cx={point.x}
		cy={point.y}
		r={radius(point)}
			visible={visible(point.properties)}
			{showHighlights}
			highlighted={highlighted(point.properties)}
			annotated={annotated(point.properties)}
			{color}
			{highlightColor}
			{greyedOut}
			{radiusFactor}
			noHighlightOpacity={layout === "map" ? 0.5 : 1}
			on:mouseover={() => {
				console.log(point.properties);
			}}
			transitionIn={sizedUpOnEntry && sizedUp()}
		/>
	{/each}
</g>
